import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { EditableTradeItem } from 'store/services/trade/tradeApi';
import { TradeItem } from '../ActiveTradesPage';
import { formatLanguage } from 'constants/languages';
import MagicButton from 'components/MagicButton/MagicButton';
import './EditTradeItemModal.css';

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM', 'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP', 'LIGHTLY_PLAYED': 'LP', 'LP': 'LP', 'SP': 'LP',
    'MODERATELY_PLAYED': 'MP', 'MP': 'MP',
    'HEAVILY_PLAYED': 'HP', 'HP': 'HP',
    'DAMAGED': 'DMG', 'DMG': 'DMG',
  };
  return conditionMap[condition] || condition;
};

const ALL_CONDITIONS = [
  { value: 'NEAR_MINT', label: 'NM - Near Mint' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP - Lightly Played' },
  { value: 'MODERATELY_PLAYED', label: 'MP - Moderately Played' },
];

interface EditValues {
  cardId: number;
  setCode: string;
  setName: string;
  condition: string;
  language: string;
  foil: boolean;
  imageUrl: string;
  availableInFoil: boolean;
  availableInNonFoil: boolean;
}

interface Props {
  item: TradeItem;
  editOptions: EditableTradeItem;
  onSave: (itemId: string, updates: { cardId: number; condition: string; language: string; foil: boolean }) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export default function EditTradeItemModal({ item, editOptions, onSave, onCancel, isSaving }: Props) {
  const prefs = editOptions.preferences!;
  const [calculatePrice] = useCalculatePriceMutation();

  const [editValues, setEditValues] = useState<EditValues>(() => {
    const currentSetDetail = prefs.acceptableSetDetails.find(s => s.cardId === editOptions.cardId);
    return {
      cardId: editOptions.cardId,
      setCode: item.setCode || currentSetDetail?.code || '',
      setName: item.set,
      condition: editOptions.currentCondition,
      language: editOptions.currentLanguage,
      foil: editOptions.currentFoil,
      imageUrl: item.image,
      availableInFoil: currentSetDetail?.availableInFoil ?? false,
      availableInNonFoil: currentSetDetail?.availableInNonFoil ?? true,
    };
  });

  const [previewPrice, setPreviewPrice] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const originalPrice = editOptions.currentPointValue;

  const hasChanges = editValues.cardId !== editOptions.cardId ||
    editValues.condition !== editOptions.currentCondition ||
    editValues.language !== editOptions.currentLanguage ||
    editValues.foil !== editOptions.currentFoil;

  const recalculatePrice = useCallback(async (values: EditValues) => {
    if (!editOptions.wantListId) return;
    setIsCalculating(true);
    try {
      const result = await calculatePrice({
        cards: [{
          wantListId: editOptions.wantListId,
          cardId: values.cardId.toString(),
          condition: values.condition,
          language: values.language,
          foil: values.foil,
          set: values.setName,
        }]
      }).unwrap();
      const newPrice = result.cards.find(c => c.wantListId === editOptions.wantListId);
      if (newPrice) {
        setPreviewPrice(newPrice.displayPrice);
      }
    } catch {
      setPreviewPrice(null);
    } finally {
      setIsCalculating(false);
    }
  }, [calculatePrice, editOptions.wantListId]);

  useEffect(() => {
    if (hasChanges) {
      recalculatePrice(editValues);
    } else {
      setPreviewPrice(null);
    }
  }, [editValues, hasChanges, recalculatePrice]);

  const updateField = (updates: Partial<EditValues>) => {
    setEditValues(prev => ({ ...prev, ...updates }));
  };

  const handleSetChange = (selectedValue: string) => {
    const selectedSetDetail = prefs.acceptableSetDetails.find(s => {
      const optionValue = s.cardId ? `${s.code}-${s.cardId}` : s.code;
      return optionValue === selectedValue;
    });
    if (!selectedSetDetail) return;

    const newAvailableInFoil = selectedSetDetail.availableInFoil ?? false;
    const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? true;

    const updates: Partial<EditValues> = {
      setCode: selectedSetDetail.code,
      setName: selectedSetDetail.name,
      cardId: selectedSetDetail.cardId,
      availableInFoil: newAvailableInFoil,
      availableInNonFoil: newAvailableInNonFoil,
    };

    if (selectedSetDetail.imageUrl) {
      updates.imageUrl = selectedSetDetail.imageUrl;
    }

    const canOfferFoil = newAvailableInFoil && (prefs.acceptFoil ?? true);
    const canOfferNonFoil = newAvailableInNonFoil && (prefs.acceptNonFoil ?? true);

    if (editValues.foil && !canOfferFoil && canOfferNonFoil) {
      updates.foil = false;
    } else if (!editValues.foil && !canOfferNonFoil && canOfferFoil) {
      updates.foil = true;
    }

    updateField(updates);
  };

  const handleSave = () => {
    onSave(editOptions.itemId, {
      cardId: editValues.cardId,
      condition: editValues.condition,
      language: editValues.language,
      foil: editValues.foil,
    });
  };

  const canOfferFoil = editValues.availableInFoil && (prefs.acceptFoil ?? true);
  const canOfferNonFoil = editValues.availableInNonFoil && (prefs.acceptNonFoil ?? true);
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  const acceptableConditions = prefs.acceptableConditions?.length
    ? ALL_CONDITIONS.filter(c => prefs.acceptableConditions.includes(c.value))
    : ALL_CONDITIONS;

  const currentSetValue = editValues.cardId
    ? `${editValues.setCode}-${editValues.cardId}`
    : editValues.setCode;

  const displayPrice = previewPrice ?? originalPrice;
  const perCardDelta = hasChanges && previewPrice !== null ? previewPrice - originalPrice : 0;
  const totalDelta = perCardDelta * item.qty;

  const modalContent = (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="edit-trade-item-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h3 className="edit-modal-title">Edit Trade Item</h3>
          <button className="edit-modal-close" onClick={onCancel}>
            <img src={AssetUrls.icons.dropdowns.close} alt="Close" style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div className="edit-modal-content">
          {/* Card preview */}
          <div className="edit-modal-card-preview">
            <FoilCardImage
              src={AssetUrls.cards.ensureCDNUrl(editValues.imageUrl)?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || ''}
              alt={item.name}
              className="edit-modal-card-image"
              foil={editValues.foil}
              borderRadius="6px"
            />
            <div className="edit-modal-card-info">
              <span className="edit-modal-card-name">{item.name}</span>
              <span className="edit-modal-card-qty">Qty: {item.qty}</span>
            </div>
          </div>

          {/* Edit fields */}
          <div className="edit-modal-fields">
            {/* Set */}
            <div className="edit-modal-field">
              <label className="edit-modal-label">Set</label>
              {prefs.acceptableSetDetails.length > 1 ? (
                <select
                  value={currentSetValue}
                  onChange={(e) => handleSetChange(e.target.value)}
                  className="edit-modal-select"
                  disabled={isSaving}
                >
                  {[...prefs.acceptableSetDetails]
                    .sort((a, b) => {
                      const nameCompare = a.name.localeCompare(b.name);
                      if (nameCompare !== 0) return nameCompare;
                      const aNum = parseInt(a.collectorNumber || '0', 10);
                      const bNum = parseInt(b.collectorNumber || '0', 10);
                      return aNum - bNum;
                    })
                    .map(setDetail => {
                      const uniqueValue = setDetail.cardId
                        ? `${setDetail.code}-${setDetail.cardId}`
                        : setDetail.code;
                      const displayText = setDetail.collectorNumber
                        ? `${setDetail.name} #${setDetail.collectorNumber}`
                        : setDetail.name;
                      return (
                        <option key={uniqueValue} value={uniqueValue}>
                          {displayText}
                        </option>
                      );
                    })}
                </select>
              ) : (
                <span className="edit-modal-static">{editValues.setName}</span>
              )}
            </div>

            {/* Foil */}
            <div className="edit-modal-field">
              <label className="edit-modal-label">Foil</label>
              {showFoilDropdown ? (
                <select
                  value={editValues.foil ? 'foil' : 'non-foil'}
                  onChange={(e) => updateField({ foil: e.target.value === 'foil' })}
                  className="edit-modal-select"
                  disabled={isSaving}
                >
                  <option value="non-foil">Non-Foil</option>
                  <option value="foil">Foil</option>
                </select>
              ) : (
                <span className="edit-modal-static">{canOfferFoil ? 'Foil' : 'Non-Foil'}</span>
              )}
            </div>

            {/* Condition */}
            <div className="edit-modal-field">
              <label className="edit-modal-label">Condition</label>
              {acceptableConditions.length > 1 ? (
                <select
                  value={editValues.condition}
                  onChange={(e) => updateField({ condition: e.target.value })}
                  className="edit-modal-select"
                  disabled={isSaving}
                >
                  {acceptableConditions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className="edit-modal-static">{formatCondition(editValues.condition)}</span>
              )}
            </div>

            {/* Language */}
            <div className="edit-modal-field">
              <label className="edit-modal-label">Language</label>
              {prefs.acceptableLanguages?.length > 1 ? (
                <select
                  value={editValues.language}
                  onChange={(e) => updateField({ language: e.target.value })}
                  className="edit-modal-select"
                  disabled={isSaving}
                >
                  {prefs.acceptableLanguages.map(lang => (
                    <option key={lang} value={lang}>{formatLanguage(lang)}</option>
                  ))}
                </select>
              ) : (
                <span className="edit-modal-static">{formatLanguage(editValues.language)}</span>
              )}
            </div>
          </div>

          {/* Price summary */}
          <div className="edit-modal-price-summary">
            <div className="edit-modal-price-row">
              <span className="edit-modal-price-label">Current Price:</span>
              <Points points={originalPrice * item.qty} type="small" />
            </div>
            {hasChanges && previewPrice !== null && (
              <>
                <div className="edit-modal-price-row">
                  <span className="edit-modal-price-label">New Price:</span>
                  <Points points={displayPrice * item.qty} type="small" />
                </div>
                {totalDelta !== 0 && (
                  <div className="edit-modal-price-row">
                    <span className="edit-modal-price-label">Change:</span>
                    <span className={`edit-modal-price-delta ${totalDelta > 0 ? 'positive' : 'negative'}`}>
                      {totalDelta > 0 ? '+' : ''}{totalDelta} pts
                      {item.qty > 1 && ` (${perCardDelta > 0 ? '+' : ''}${perCardDelta}/ea)`}
                    </span>
                  </div>
                )}
              </>
            )}
            {isCalculating && (
              <div className="edit-modal-price-row">
                <span className="edit-modal-price-label">Calculating...</span>
              </div>
            )}
          </div>
        </div>

        <div className="edit-modal-footer">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onCancel}
            isDisabled={isSaving}
          />
          <MagicButton
            label={isSaving ? "Saving..." : "Save Changes"}
            color="purple"
            onPress={handleSave}
            isDisabled={isSaving || isCalculating || !hasChanges}
            isLoading={isSaving}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
